import { Component, EventEmitter, HostListener, Input, OnInit, Optional, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { throwError, ReplaySubject } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import jwt_decode from "jwt-decode";
import { ToastrService } from 'ngx-toastr';
import { IFeatures, IGetFeaturesByRoles } from 'app/services/graphqlApi.service';
import { PanelService } from 'app/shared/panels/chart/Services/panel.service';
import { LoginService } from 'app/auth/services/login.service';
import { IUserLogin } from 'app/shared/interfaces/model';
import { EEntityTypes, EViewTypes } from 'app/shared/enums';
import { USER_ROLE } from 'app/constants';

@Component({
  selector: 'app-user-login',
  templateUrl: './user-login.component.html',
  styleUrls: ['./user-login.component.scss']
})

export class UserLoginComponent implements OnInit {
  @Input() isDelete: boolean;
  @Input() type: EEntityTypes;
  @Output() response = new EventEmitter<IUserLogin | boolean>();
  @HostListener('document:keydown.Enter', ['$event']) onKeydownHandler(event: KeyboardEvent) {
    if (event.code == 'Enter' && !this.isDelete) { this.submit(); }
  }

  readonly destroyed = new ReplaySubject(1);
  loginForm: FormGroup;
  entityTypes = EEntityTypes;
  processingLoginRequest: boolean = false;
  inputType: string = '';
  isFireFox: boolean = false;

  constructor(private fb: FormBuilder, private readonly loginService: LoginService, private readonly panelService: PanelService, private readonly router: Router, private readonly toastrService: ToastrService) { }

  ngOnInit(): void {
    if (!this.isDelete) {
      this.loginService.logOut(true);
    }

    if (this.getBrowserName() === "firefox") {
      this.isFireFox = true;
    }

    this.initLoginForm();
    // setting type password for password (to prevent intellisense)
    this.loginForm.valueChanges.subscribe(value => {
      this.inputType = (value?.password) ? 'password' : '';
    });

  }

  get isDashboardPage() {
    return this.router.url.includes(EEntityTypes.DASHBOARD);
  }

  userNameFocus() {
    this.initLoginForm();
  }

  initLoginForm() {
    this.loginForm = this.fb.group({
      userName: [
        "",
        Validators.compose([
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(320),
        ]),
      ],
      password: [
        "",
        Validators.compose([
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(100),
        ]),
      ],
    });
  }

  submit() {
    const controls = this.loginForm.controls;
    /** check form */
    if (this.loginForm.invalid) {
      Object.keys(controls).forEach((controlName) =>
        controls[controlName].markAsTouched()
      );
      return;
    }

    let userName = this.loginForm.value.userName;
    let password = this.loginForm.value.password;
    this.processingLoginRequest = true;
    this.loginService.login(userName, password).pipe(
      catchError((err) => {
        this.processingLoginRequest = false;
        return throwError(err);
      })).subscribe((loginResponse) => {
        this.processingLoginRequest = false;
        if (!!loginResponse['access_token']) {
          const tokenDetails = jwt_decode(loginResponse.access_token);
          const roles: string[] = tokenDetails['resource_access']?.intellijaui?.roles || [];
          const isSuperAdmin: boolean = (tokenDetails['preferred_username'] === "superadmin") ? true : false;
          console.log('isSuperAdmin: ', isSuperAdmin);
          if(isSuperAdmin){
            localStorage.setItem("isSuperAdmin", JSON.stringify(true));
            this.router.navigate(["/dashboard"]);
            this.panelService._viewToggle.next(EViewTypes.DASHBOARD);
          } else if (roles.length) {
            this.loginService.getFeaturesByRoles(roles).pipe(take(1)).subscribe((response: IGetFeaturesByRoles) => {
              if (response.intellija_roles?.length) {
                const features: string[] = [];
                const featuresDetail: IFeatures[] = [];
                response.intellija_roles.filter(roles => {
                  if (roles.features?.length) {
                    features.push(...roles.features);
                  }
                });

                if(features.length){
                  this.loginService.getFeaturesDetailByFeatures(features).subscribe((featuresDetails: IFeatures[]) => {
                    if (featuresDetails.length) {
                      featuresDetail.push(...featuresDetails);
                      localStorage.setItem("Features", JSON.stringify(featuresDetail));
                      this.panelService._features.next(featuresDetail);
                      console.log('featuresDetail:user-login ', featuresDetail);
                      const isAdminView: boolean = featuresDetail.every((feature: IFeatures) => feature.is_config === true); // is_config is basically checking isAdmin right
                      if (isAdminView) {
                        // start idle user tracking
                        this.loginService.startIdleUserTrack();
                        if (!this.isDelete) {
                          this.router.navigate(["/components"]);
                          this.panelService._viewToggle.next(EViewTypes.ADMIN);
                        }
                      } else {
                        // start idle user tracking
                        this.loginService.startIdleUserTrack();
                        if (!this.isDelete) {
                          this.router.navigate(["/dashboard"]);
                          this.panelService._viewToggle.next(EViewTypes.DASHBOARD);
                        }
                      }
                    }
                  });
                }else{
                  this.toastrService.error("The User doesn't have the required permission for this operation. Please contact the administrator");
                }
              }
            });
          }else{
            this.toastrService.error("The User doesn't have the required permission for this operation. Please contact the administrator");
          }
        } (err) => {
          this.processingLoginRequest = false;
        };
      });
  }

  isControlHasError(controlName: string, validationType: string): boolean {
    const control = this.loginForm.controls[controlName];
    if (!control) {
      return false;
    }
    const result =
      control.hasError(validationType) && (control.dirty || control.touched);
    return result;
  }

  /**
   * check user credentials and emit them (for delete)
   * @param value flag value
   */
  getCredentialsForDel(value?: IUserLogin | string) {
    const credentials: IUserLogin = { username: this.loginForm.value?.userName, password: this.loginForm.value?.password };
    value ? this.response.emit(false) : this.response.emit(credentials);
  }

  /**
   * et browser name
   */
  getBrowserName() {
    const agent = window.navigator.userAgent.toLowerCase()
    switch (true) {
      case agent.indexOf('edge') > -1:
        return 'edge';
      case agent.indexOf('opr') > -1 && !!(<any>window).opr:
        return 'opera';
      case agent.indexOf('chrome') > -1 && !!(<any>window).chrome:
        return 'chrome';
      case agent.indexOf('trident') > -1:
        return 'ie';
      case agent.indexOf('firefox') > -1:
        return 'firefox';
      case agent.indexOf('safari') > -1:
        return 'safari';
      default:
        return 'other';
    }
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }

}
